import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { UserRole } from '../types';
import { LoadingState } from '../components/common';

interface RouteGuardProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireSubscription?: boolean;
  requireCompanyContext?: boolean;
}

const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRole.SUPER_ADMIN]: 4,
  [UserRole.ENTERPRISE_ADMIN]: 3,
  [UserRole.EMPLOYEE]: 2,
  [UserRole.USER]: 1,
};

const hasPermission = (userRole: UserRole, requiredRoles: UserRole[]) => {
  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  return requiredRoles.some(role => userLevel >= (ROLE_HIERARCHY[role] || 0));
};

export const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  allowedRoles,
  requireSubscription = false,
  requireCompanyContext = false,
}) => {
  const { user, loading: authLoading } = useAuth();
  const { company, isSubscriptionActive, loading: tenantLoading } = useTenant();
  const location = useLocation();

  if (authLoading || tenantLoading) {
    return <LoadingState />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Validate Role
  if (allowedRoles && !hasPermission(user.role, allowedRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Validate Company Context
  if (requireCompanyContext && !company) {
    return <Navigate to="/select-company" replace />;
  }

  // Validate Subscription
  if (requireSubscription && !isSubscriptionActive) {
    return <Navigate to="/activate-enterprise" replace />;
  }

  return <>{children}</>;
};
